/**
 * Render the limited inline Markdown supported by grammar content.
 *
 * Content is escaped before formatting is applied so API-provided text cannot
 * inject arbitrary HTML. This intentionally mirrors the learner application.
 */
export function renderInlineMarkdown(text: string): string {
  if (typeof text !== "string" || !text) {
    return "";
  }

  const escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  return escaped
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>");
}

/* -------------------------------------------------------------------------
 * Cloze-dropdown answer review (NLM-Test #96)
 *
 * Mission Control has no shared package with the learner app, so this mirrors
 * `src/modules/lingo/services/grammarRenderer.ts` there. Keep the two in step:
 * the review modals on both surfaces are meant to read identically.
 *
 * Cloze items embed blank tokens of the form <<blank_1>>. The review modal
 * presents the item as whole sentences rather than a per-blank breakdown: the
 * question carries every blank's options inline in brackets, then the sentence
 * is re-read with the chosen words filled in.
 * ---------------------------------------------------------------------- */

export interface TextSegment {
  type: "text";
  value: string;
}

export interface BlankSegment {
  type: "blank";
  blankKey: string;
}

export type ClozeSegment = TextSegment | BlankSegment;

export interface ClozeOption {
  label: string;
  text: string;
}

export interface ClozeBlankDefinition {
  options: ClozeOption[];
  correct: string;
}

export type ClozeBlankMap = Record<string, ClozeBlankDefinition>;

export interface ClozeAnswerView {
  /** Authored instruction line, or "" when the item is a bare sentence. */
  instructionHtml: string;
  /** Sentence with each blank rendered as "[option, option, ...]". */
  questionHtml: string;
  /** Sentence with the learner's selection filled into each blank. */
  studentHtml: string;
  /** Sentence with the correct option filled into each blank. */
  correctHtml: string;
}

/** Placeholder for a blank the learner never answered, matching the modal's existing fallback. */
const UNANSWERED = "—";

const BLANK_TOKEN_RE = /<<blank_(\d+)>>/g;

// Deliberately not the /g regex above: that one carries lastIndex between
// calls, so a bare .test() on it would alternate true and false.
const HAS_BLANK_RE = /<<blank_\d+>>/;

/** Split a cloze template into ordered text and blank segments. */
export function parseClozeTemplate(text: string): ClozeSegment[] {
  if (typeof text !== "string" || !text) {
    return [];
  }

  const segments: ClozeSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  BLANK_TOKEN_RE.lastIndex = 0;
  while ((match = BLANK_TOKEN_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: "text", value: text.slice(lastIndex, match.index) });
    }
    segments.push({ type: "blank", blankKey: match[1] });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", value: text.slice(lastIndex) });
  }

  return segments;
}

/**
 * Split an authored cloze `text` into its instruction line and its sentence.
 *
 * Authors put any instruction inside `text` itself, ahead of the sentence and
 * separated by a line break. The first line only counts as an instruction when
 * it holds no blank of its own, otherwise it is simply the start of a sentence
 * that happens to wrap.
 */
export function splitClozeInstruction(text: string): { instruction: string; sentence: string } {
  if (typeof text !== "string" || !text) {
    return { instruction: "", sentence: "" };
  }
  const breakIndex = text.indexOf("\n");
  if (breakIndex === -1) {
    return { instruction: "", sentence: text };
  }
  const head = text.slice(0, breakIndex);
  const tail = text.slice(breakIndex + 1).replace(/^\s*\n+/, "");
  if (!tail.trim() || HAS_BLANK_RE.test(head)) {
    return { instruction: "", sentence: text };
  }
  return { instruction: head.trim(), sentence: tail };
}

/** Resolve an option label ("A") to its authored text. */
export function clozeOptionText(options: ClozeOption[] | undefined, label: string): string {
  if (!Array.isArray(options) || !label) return "";
  const match = options.find(option => option && option.label === label);
  return match && typeof match.text === "string" ? match.text : "";
}

function renderSentence(sentence: string, fillBlank: (blankKey: string) => string): string {
  return parseClozeTemplate(sentence)
    .map(segment =>
      segment.type === "text" ? renderInlineMarkdown(segment.value) : fillBlank(segment.blankKey)
    )
    .join("");
}

/**
 * Build the three sentences the answer review modal renders for a cloze item.
 *
 * `selections` maps blank key to the option label the learner picked, in the
 * same shape the exposure entry stores.
 */
export function buildClozeAnswerView(
  text: string,
  blanks: ClozeBlankMap | undefined,
  selections: Record<string, string> | undefined
): ClozeAnswerView {
  const safeBlanks: ClozeBlankMap = blanks && typeof blanks === "object" ? blanks : {};
  const safeSelections = selections && typeof selections === "object" ? selections : {};
  const { instruction, sentence } = splitClozeInstruction(text);

  const optionsFor = (key: string) => (safeBlanks[key] && safeBlanks[key].options) || [];

  const questionHtml = renderSentence(sentence, key => {
    const texts = optionsFor(key)
      .map(option => (option && typeof option.text === "string" ? option.text : ""))
      .filter(Boolean);
    if (!texts.length) return "[]";
    return `[${texts.map(renderInlineMarkdown).join(", ")}]`;
  });

  const filled = (resolveLabel: (key: string) => string) => (key: string) => {
    const value = clozeOptionText(optionsFor(key), resolveLabel(key));
    return `<strong>${value ? renderInlineMarkdown(value) : UNANSWERED}</strong>`;
  };

  return {
    instructionHtml: instruction ? renderInlineMarkdown(instruction) : "",
    questionHtml,
    studentHtml: renderSentence(sentence, filled(key => safeSelections[key] || "")),
    correctHtml: renderSentence(sentence, filled(key => (safeBlanks[key] && safeBlanks[key].correct) || ""))
  };
}
